import React, { useEffect, useMemo, useState } from 'react';
import ProfaseModal from '../../commons/ProfaseModal';
import PageForm, { FormField } from '../../commons/PageForm';
import {
  buildAnioOptions,
  calcularDiaVencimiento,
  CalcularDiaVencimientoResponse,
  extractDiaVencimientoError,
  formatFechaVencimientoLong,
  MESES_PERIODO,
} from '../../shared/dia-vencimiento';

export interface CalcularVencimientoForm {
  rfc: string;
  mes: string;
  anio: string;
  menosDias: string;
  masDias: string;
}

function defaultForm(): CalcularVencimientoForm {
  const now = new Date();
  return {
    rfc: '',
    mes: String(now.getMonth() + 1),
    anio: String(now.getFullYear()),
    menosDias: '',
    masDias: '',
  };
}

const ACCENT = '#6366f1';

const layoutStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 20,
  alignItems: 'stretch',
  marginTop: 4,
};

const formColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
};

const resultPanelStyle: React.CSSProperties = {
  padding: '16px 18px',
  borderRadius: 10,
  background: 'var(--crm-surface-muted, #f8fafc)',
  border: '1px solid var(--crm-border)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  height: '100%',
  boxSizing: 'border-box',
};

const resultPanelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--crm-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const resultPlaceholderStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--crm-text-muted)',
  lineHeight: 1.5,
};

const resultMainStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 700,
  color: 'var(--crm-text-primary)',
  lineHeight: 1.35,
};

const resultSecondaryStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--crm-text-secondary)',
  lineHeight: 1.45,
};

const errorStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 8,
  background: 'var(--crm-form-error-bg, #fef2f2)',
  color: 'var(--crm-form-error-text, #b91c1c)',
  fontSize: 13,
};

export interface CalcularVencimientoModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CalcularVencimientoModal({ open, onClose }: CalcularVencimientoModalProps) {
  const [form, setForm] = useState<CalcularVencimientoForm>(() => defaultForm());
  const [errors, setErrors] = useState<Partial<Record<keyof CalcularVencimientoForm, string>>>({});
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState<CalcularDiaVencimientoResponse | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const anioOptions = useMemo(() => buildAnioOptions(), []);

  const fields = useMemo<FormField<CalcularVencimientoForm>[]>(
    () => [
      {
        name: 'rfc',
        label: 'RFC',
        type: 'text',
        required: true,
        placeholder: 'Ej. AAA010101AAA',
        hint: 'RFC del contribuyente para el cálculo SAT',
      },
      {
        name: 'mes',
        label: 'Mes del periodo',
        type: 'select',
        required: true,
        options: [...MESES_PERIODO],
      },
      {
        name: 'anio',
        label: 'Año del periodo',
        type: 'select',
        required: true,
        options: anioOptions,
      },
      {
        name: 'menosDias',
        label: '−x días hábiles',
        type: 'number',
        placeholder: 'Opcional',
        hint: 'Días hábiles antes del vencimiento SAT',
      },
      {
        name: 'masDias',
        label: '+x días hábiles',
        type: 'number',
        placeholder: 'Opcional',
        hint: 'Días hábiles después del vencimiento SAT',
      },
    ],
    [anioOptions],
  );

  useEffect(() => {
    if (!open) return;
    setForm(defaultForm());
    setErrors({});
    setResult(null);
    setApiError(null);
  }, [open]);

  const handleFieldChange = (name: keyof CalcularVencimientoForm, value: string) => {
    const nextValue = name === 'rfc' ? value.toUpperCase() : value;
    setForm(prev => ({ ...prev, [name]: nextValue }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
    setApiError(null);
  };

  const validate = (): Partial<Record<keyof CalcularVencimientoForm, string>> => {
    const errs: Partial<Record<keyof CalcularVencimientoForm, string>> = {};
    if (!form.rfc.trim()) errs.rfc = 'Ingrese un RFC';
    const mes = Number(form.mes);
    if (Number.isNaN(mes) || mes < 1 || mes > 12) errs.mes = 'Seleccione un mes válido';
    const anio = Number(form.anio);
    if (Number.isNaN(anio) || anio < 1900 || anio > 2100) errs.anio = 'Seleccione un año válido';

    const menosTrim = form.menosDias.trim();
    if (menosTrim !== '') {
      const menos = Number(menosTrim);
      if (Number.isNaN(menos) || menos < 0 || !Number.isInteger(menos)) {
        errs.menosDias = 'Debe ser un entero mayor o igual a 0';
      }
    }

    const masTrim = form.masDias.trim();
    if (masTrim !== '') {
      const mas = Number(masTrim);
      if (Number.isNaN(mas) || mas < 0 || !Number.isInteger(mas)) {
        errs.masDias = 'Debe ser un entero mayor o igual a 0';
      }
    }

    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setCalculating(true);
    setApiError(null);
    setResult(null);

    const menosTrim = form.menosDias.trim();
    const masTrim = form.masDias.trim();

    try {
      const data = await calcularDiaVencimiento({
        rfc: form.rfc.trim(),
        mes: Number(form.mes),
        anio: Number(form.anio),
        menosDias: menosTrim === '' ? null : Number(menosTrim),
        masDias: masTrim === '' ? null : Number(masTrim),
      });
      setResult(data);
    } catch (err) {
      setApiError(extractDiaVencimientoError(err));
    } finally {
      setCalculating(false);
    }
  };

  if (!open) return null;

  return (
    <ProfaseModal
      open
      size="large"
      titleId="calcular-vencimiento-modal-title"
    >
      <h2 id="calcular-vencimiento-modal-title" className="profase-modal-title">
        Calcular vencimiento
      </h2>
      <p className="profase-modal-description">
        Simula la fecha de vencimiento SAT para un periodo fiscal y RFC. Opcionalmente aplica desplazamiento
        en días hábiles (−x / +x).
      </p>

      <div style={layoutStyle}>
        <div style={formColumnStyle}>
          <PageForm
            embedded
            fields={fields}
            values={form}
            errors={errors}
            isNew
            saving={calculating}
            onFieldChange={handleFieldChange}
            onSubmit={handleSubmit}
            onCancel={onClose}
            accentColor={ACCENT}
            submitLabel="Calcular"
            cancelLabel="Cerrar"
          />

          {apiError && <div style={errorStyle}>{apiError}</div>}
        </div>

        <aside style={resultPanelStyle} aria-live="polite">
          <h3 style={resultPanelTitleStyle}>Resultado</h3>

          {calculating && (
            <p style={resultPlaceholderStyle}>Calculando vencimiento…</p>
          )}

          {!calculating && !result && !apiError && (
            <p style={resultPlaceholderStyle}>
              Ingrese RFC, periodo y pulse Calcular para ver la fecha de vencimiento aquí.
            </p>
          )}

          {!calculating && result && (
            <>
              <p style={resultMainStyle}>{formatFechaVencimientoLong(result.fechaVencimiento)}</p>
              <p style={resultSecondaryStyle}>
                Vencimiento SAT del periodo {MESES_PERIODO[result.mes - 1]?.label ?? result.mes} {result.anio}
              </p>

              {result.diasAdicionalesRfc > 0 && (
                <p style={resultSecondaryStyle}>
                  Facilidad por RFC: +{result.diasAdicionalesRfc} día{result.diasAdicionalesRfc === 1 ? '' : 's'} hábil{result.diasAdicionalesRfc === 1 ? '' : 'es'}
                </p>
              )}

              {result.fechaMenosDias && (
                <p style={resultSecondaryStyle}>
                  −{form.menosDias.trim() || 'x'} días hábiles: {formatFechaVencimientoLong(result.fechaMenosDias)}
                </p>
              )}

              {result.fechaMasDias && (
                <p style={resultSecondaryStyle}>
                  +{form.masDias.trim() || 'x'} días hábiles: {formatFechaVencimientoLong(result.fechaMasDias)}
                </p>
              )}
            </>
          )}
        </aside>
      </div>
    </ProfaseModal>
  );
}
